import { Card, Col, Row, Select, Spin, Typography } from "antd";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { meiliClient } from "../../utils/meiliClient";
import { toMeiliFilter, FilterRow } from "../../types/meili";
import { useGetJobsByRecruiter } from "api/jobs/useGetJobsByRecruiter";
import { storage } from "utils/storage";
import { APPLICATION_STATUS } from "enum/job-status-type";
import { nepalProvincesData } from "./nepal-provinces.geojson";
import { Feature } from "geojson";

// Import Leaflet and React-Leaflet components
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from "react-leaflet";
import L, { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";

// Import your existing CSS for popups and markers
import "./index.css";

// --- TypeScript Interfaces ---
interface LocationFilters {
  jobId?: string;
  status?: string;
  province?: string;
  gender?: string;
}

interface CustomFeature extends Feature {
  properties: { Province: string };
  geometry: { type: "Polygon" | "MultiPolygon"; coordinates: any };
}

interface Candidate {
  id: string;
  status: string;
  jobId?: string;
  jobTitle?: string;
  applicant_info_firstName: string;
  applicant_info_lastName: string;
  applicant_info_email: string;
  applicant_info_phoneNumber: string;
  applicant_info_permanentAddress_province: string;
  applicant_info_permanentAddress_lat?: number;
  applicant_info_permanentAddress_lng?: number;
}

interface CandidatePoint extends Candidate {
  lat: number;
  lng: number;
  color: string;
}

const { Title } = Typography;

// --- Color and Data Mappings ---
const provinceColors: string[] = ["#A3D9A5", "#F6CBA1", "#8EC6E6", "#F8BBD0", "#FFD699", "#C4B6F1", "#9FD8CB"];
const statusColors: { [key: string]: string } = {
  [APPLICATION_STATUS.APPROVED]: "#22c55e",
  [APPLICATION_STATUS.SHORTLISTED]: "#3b82f6",
  [APPLICATION_STATUS.SUBMITTED]: "#facc15",
  [APPLICATION_STATUS.REJECTED]: "#ef4444",
};
const mapGeoToMeili: { [key: string]: string } = {
  Koshi: "Koshi Province", Madesh: "Madhesh Province", Bagmati: "Bagmati Province",
  Gandaki: "Gandaki Province", Lumbini: "Lumbini Province", Karnali: "Karnali Province",
  "Sudur Pashchim": "Sudurpashchim Province",
};

// --- Geometry helpers for the fallback mechanism ---
const isPointInPolygon = (point: [number, number], polygon: number[][]): boolean => {
  const [x, y] = point;
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
    if (intersect) isInside = !isInside;
  }
  return isInside;
};

const getBoundingBox = (polygon: number[][]) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of polygon) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
};

const getRandomPointInPolygon = (polygon: number[][]): [number, number] | null => {
  const bbox = getBoundingBox(polygon);
  for (let i = 0; i < 100; i++) {
    const lat = bbox.minY + Math.random() * (bbox.maxY - bbox.minY);
    const lng = bbox.minX + Math.random() * (bbox.maxX - bbox.minX);
    if (isPointInPolygon([lng, lat], polygon)) return [lng, lat];
  }
  return null;
};

// --- API Fetching Logic ---
const fetchAllCandidatesFromMeili = async (filters: LocationFilters) => {
    const filterRows: FilterRow[] = [];
    if (filters.jobId && filters.jobId !== "all") filterRows.push({ field: "jobId", op: "=", value: filters.jobId });
    if (filters.status) filterRows.push({ field: "status", op: "=", value: filters.status });
    if (filters.province) {
      const meiliProvinceName = mapGeoToMeili[filters.province] || filters.province;
      filterRows.push({ field: "applicant_info_permanentAddress_province", op: "=", value: meiliProvinceName });
    }
    if (filters.gender) filterRows.push({ field: "applicant_info_gender", op: "=", value: filters.gender });
    
    const filterString = toMeiliFilter(filterRows).join(" AND ");
    const index = meiliClient.index("applications");

    const attributesToRetrieve = [ 
        "id", "status", "jobId", "jobTitle",
        "applicant_info_firstName", "applicant_info_lastName", 
        "applicant_info_email", "applicant_info_phoneNumber",
        "applicant_info_permanentAddress_province",
        "applicant_info_permanentAddress_lat",
        "applicant_info_permanentAddress_lng",
    ];
    
    const searchResult = await index.search("", {
      filter: filterString,
      limit: 2000,
      attributesToRetrieve,
    });
    return searchResult.hits as Candidate[];
};


// --- Leaflet Marker Icon Helper ---
const createCustomIcon = (color: string) => {
  return new L.DivIcon({
    html: `<span class="custom-marker-dot" style="background-color: ${color};"></span>`,
    className: "custom-marker-icon",
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

const Dashboard = () => {
  // --- State and Hooks ---
  const [selectedJob, setSelectedJob] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [selectedProvince, setSelectedProvince] = useState<string | undefined>();
  const [selectedGender, setSelectedGender] = useState<string | undefined>();

  const user = storage.getUserFromStorage();
  const { data: jobsList } = useGetJobsByRecruiter({ recruiterId: user.uid });

  const filters: LocationFilters = { jobId: selectedJob, status: selectedStatus, province: selectedProvince, gender: selectedGender, };

  const { data: candidatesData, isLoading, isError } = useQuery({
    queryKey: ["allCandidates", filters],
    queryFn: () => fetchAllCandidatesFromMeili(filters),
  });

  const features = nepalProvincesData.features as CustomFeature[];

  const provinceFeatureMap = useMemo(() => {
    const map = new Map<string, CustomFeature>();
    features.forEach(f => {
      const meiliName = mapGeoToMeili[f.properties.Province] || f.properties.Province;
      map.set(meiliName, f);
    });
    return map;
  }, [features]);

  const jobMap = useMemo(() => {
    const map = new Map<string, string>();
    jobsList?.data?.forEach(job => map.set(job.id, job.jobTitle));
    return map;
  }, [jobsList]);

  // CRITICAL FIX: This logic shows all candidates. It uses exact coordinates
  // if they exist, or falls back to a random point in the province if they don't.
  const candidatePoints = useMemo((): CandidatePoint[] => {
    if (!candidatesData) return [];
    
    return candidatesData.map(candidate => {
      const color = statusColors[candidate.status] || "#888888";
      const lat = candidate.applicant_info_permanentAddress_lat;
      const lng = candidate.applicant_info_permanentAddress_lng;

      // Primary approach: Use exact coordinates if they exist.
      if (lat && lng) {
        return { ...candidate, lat, lng, color };
      }

      // Fallback approach: If no coordinates, find a random point in the province.
      const provinceFeature = provinceFeatureMap.get(candidate.applicant_info_permanentAddress_province);
      if (provinceFeature) {
        const polygonCoordinates = provinceFeature.geometry.type === 'Polygon' 
            ? provinceFeature.geometry.coordinates[0] 
            : provinceFeature.geometry.coordinates[0][0];

        const randomPoint = getRandomPointInPolygon(polygonCoordinates);
        if (randomPoint) {
          const [randomLng, randomLat] = randomPoint;
          return { ...candidate, lat: randomLat, lng: randomLng, color };
        }
      }
      
      return null;
    }).filter((p): p is CandidatePoint => p !== null);
  }, [candidatesData, provinceFeatureMap]);


  if (isError) return <div>Error loading data. Please try again later.</div>;

  // --- Options for Filters ---
  const jobOptions = [ { value: "all", label: "All Jobs" }, ...(jobsList?.data?.map((job) => ({ value: job.id, label: job.jobTitle })) || []), ];
  const provinceOptions = features.map((f: CustomFeature) => ({ value: f.properties.Province, label: f.properties.Province, }));
  const statusOptions = Object.values(APPLICATION_STATUS).filter(status => !!status && status !== APPLICATION_STATUS.ON_VIEW && status !== APPLICATION_STATUS.SAVED && status !== APPLICATION_STATUS.PENDING).map((status) => ({ value: status, label: status }));

  const getProvinceStyle = (feature?: CustomFeature) => {
    const provinceName = feature?.properties.Province || '';
    const provinceIndex = features.findIndex(f => f.properties.Province === provinceName);
    return { fillColor: provinceColors[provinceIndex] || "#ccc", weight: 1.5, color: "white", fillOpacity: 0.65, };
  };

  const nepalBounds: LatLngBoundsExpression = [ [26.3, 79.8], [30.6, 88.5], ];

  return (
    <div className="bg-background">
      <div className="max-w-6xl mx-auto py-12">
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={2}>Candidate Location Map</Title>
            <Title level={4} type="secondary">Showing {candidatePoints.length} Candidates</Title>
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}><Select allowClear placeholder="Filter by Job" style={{ width: "100%" }} value={selectedJob} onChange={setSelectedJob} options={jobOptions} /></Col>
            <Col xs={24} sm={12} md={6}><Select allowClear placeholder="All Statuses" style={{ width: "100%" }} value={selectedStatus} onChange={setSelectedStatus} options={statusOptions} /></Col>
            <Col xs={24} sm={12} md={6}><Select allowClear placeholder="Filter by Province" style={{ width: "100%" }} value={selectedProvince} onChange={setSelectedProvince} options={provinceOptions} /></Col>
            <Col xs={24} sm={12} md={6}><Select allowClear placeholder="Filter by Gender" style={{ width: "100%" }} value={selectedGender} onChange={setSelectedGender} options={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "other", label: "Other" }]} /></Col>
          </Row>

          <div style={{ position: "relative", borderRadius: "8px", overflow: "hidden", border: "1px solid #f0f0f0" }}>
            {isLoading && ( <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(255,255,255,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size="large" /></div> )}
            
            <MapContainer center={[28.45, 84.2]} zoom={7} minZoom={7} scrollWheelZoom={true} style={{ height: "600px", width: "100%", zIndex: 1 }} maxBounds={nepalBounds} maxBoundsViscosity={1.0} >
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <GeoJSON data={nepalProvincesData as any} style={getProvinceStyle} />
              
              {candidatePoints.map(point => (
                <Marker key={point.id} position={[point.lat, point.lng]} icon={createCustomIcon(point.color)}>
                  <Popup>
                    <div className="map-popup-content">
                      <div className="font-semibold mb-1">{point.applicant_info_firstName} {point.applicant_info_lastName}</div>
                      <div className="text-sm">{point.applicant_info_email}</div>
                      <div className="text-sm">{point.applicant_info_phoneNumber}</div>
                      <div className="text-sm mt-1"><strong>Job:</strong> {point.jobTitle || jobMap.get(point.jobId || "") || "N/A"}</div>
                      <div className="text-sm font-bold" style={{ color: point.color }}>Status: {point.status}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;